import { ref, watch } from 'vue'
import type { FlightArea, SaveFlightArea, UpdateFlightArea } from '@/types/flightArea'
import {
  fetchFlightAreas,
  createFlightArea,
  updateFlightArea,
  deleteFlightArea,
  type FlightAreaResponse,
} from '@/api/flightAreasApi'
import { MAX_FLIGHT_AREA_COUNT } from '@/constants'
import { useFlightAreaStore } from '@/stores/flightArea'

export function useFlightAreasLogic() {
  const isSaving = ref(false)
  const originalSnapshot = ref<FlightArea[]>([])
  const isDirty = ref(false)
  const activeTab = ref(1)

  const flightAreaStore = useFlightAreaStore()

  //エリアが登録されていない際の空オブジェクト
  const emptyFlightArea = (areaNo = 0): FlightArea => ({
    flight_area_id: 0,
    area_no: areaNo,
    flight_area_name: '',
    center_point_coordinate: {
      latitude: null,
      longitude: null,
    },
    comment: '',
  })

  const flightArea = ref<FlightArea>(emptyFlightArea(1))

  const flightAreas = ref<FlightArea[]>(
    Array.from({ length: MAX_FLIGHT_AREA_COUNT }, (_, i) => emptyFlightArea(i + 1)),
  )

  function setFlightArea() {
    const area =
      originalSnapshot.value.find((a) => a.area_no === activeTab.value) ??
      emptyFlightArea(activeTab.value)
    flightArea.value = { ...area, center_point_coordinate: { ...area.center_point_coordinate } }
  }

  function setSnapshot() {
    originalSnapshot.value = flightAreas.value.map((a) => ({
      ...a,
      center_point_coordinate: { ...a.center_point_coordinate },
    }))
  }

  function isChanged(): boolean {
    return (
      JSON.stringify(flightArea.value) !==
      JSON.stringify(originalSnapshot.value[activeTab.value - 1])
    )
  }

  // flightArea を監視して isDirty を自動更新
  watch(
    flightArea,
    () => {
      isDirty.value = isChanged()
    },
    { deep: true },
  )

  // タブが変更されたときに飛行エリアを再設定
  watch(activeTab, () => {
    setFlightArea()
  })

  // 取得
  const loadFlightAreas = async () => {
    const flightAreasList = await fetchFlightAreas()
    // 取得したエリア配列は登録されているエリアのみのため、エリアNOで一致させてセットする
    flightAreas.value = Array.from({ length: MAX_FLIGHT_AREA_COUNT }, (_, i) => {
      const areaNo = i + 1
      const found = flightAreasList.find((a) => a.area_no === areaNo)
      return found ? found : emptyFlightArea(areaNo)
    })
    setSnapshot()
    setFlightArea()
  }

  // 保存
  async function saveFlightArea() {
    isSaving.value = true
    let response
    try {
      if (flightArea.value.flight_area_id !== 0) {
        // 更新
        const body: UpdateFlightArea = {
          ...flightArea.value,
          center_point_coordinate: {
            latitude: Number(flightArea.value.center_point_coordinate.latitude),
            longitude: Number(flightArea.value.center_point_coordinate.longitude),
          },
        }

        response = await updateFlightArea(flightArea.value.flight_area_id, body)
      } else {
        // 新規登録
        const body: SaveFlightArea = {
          area_no: flightArea.value.area_no,
          flight_area_name: flightArea.value.flight_area_name,
          center_point_coordinate: {
            latitude: Number(flightArea.value.center_point_coordinate.latitude),
            longitude: Number(flightArea.value.center_point_coordinate.longitude),
          },
          comment: flightArea.value.comment,
        }

        response = await createFlightArea(body)
      }
      await flightAreaStore.fetchFlightAreas()
    } finally {
      isSaving.value = false
    }
    return response
  }

  // 削除
  async function removeFlightArea(): Promise<FlightAreaResponse> {
    const response = await deleteFlightArea(flightArea.value.flight_area_id)
    await flightAreaStore.fetchFlightAreas()
    return response
  }

  return {
    flightArea,
    activeTab,
    isDirty,
    isSaving,
    loadFlightAreas,
    saveFlightArea,
    removeFlightArea,
  }
}
